import React from 'react';
import { Dropdown as AntdDropdown, message } from 'antd';
import styled from 'styled-components';
import { Icon, Button } from 'components';
import { syncBuyerShowings, syncListingShowings } from 'api/AlmanacController';
import errorReporter from '@app/errorReporter';
import AgentUserCalendarContext from '../AgentUserCalendarContext';
import { AgentCalendarMenu, ListingCalendarMenu } from '../types';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';

const CalendarSyncButton: React.FC = () => {
  const {
    listingId,
    distinctListings,
    selectedMenuOption,
    listingsToSync: listingIds,
    selectedAppointments: appointmentIds,
    setListingsToSync,
    setCalendarModalVisible,
    setSyncByListingModalVisible,
  } = useShowingsCalendarContext();
  const agentUserCalendarContext = React.useContext(AgentUserCalendarContext);
  const [loading, setLoading] = React.useState(false);

  const onSyncClick = async () => {
    if (!agentUserCalendarContext?.isAuthenticated) {
      if (
        selectedMenuOption === ListingCalendarMenu.InPerson &&
        !appointmentIds?.length
      ) {
        setListingsToSync([listingId]);
      }
      setCalendarModalVisible(true);
      return;
    }

    try {
      setLoading(true);
      if (selectedMenuOption === AgentCalendarMenu.Listing) {
        await syncListingShowings({ appointmentIds, listingIds });
      } else if (selectedMenuOption === AgentCalendarMenu.Buyer) {
        await syncBuyerShowings({ appointmentIds });
      } else if (selectedMenuOption === ListingCalendarMenu.InPerson) {
        if (appointmentIds.length) {
          await syncListingShowings({ appointmentIds });
        } else {
          await syncListingShowings({ listingIds: [listingId] });
        }
      }
    } catch (err) {
      errorReporter.error(
        `Error syncing calendar appointment(s)/listing(s) with authenticated user: ${err}`,
      );
      message.error('Error syncing calendar appointment(s)/listing(s)');
    } finally {
      message.success('Calendar synced successfully');
      setLoading(false);
    }
  };

  const buttonText = 'Sync';
  if (selectedMenuOption === 'Listing') {
    return (
      <AntdDropdown.Button
        icon={
          <Icon type="antdDownOutlined" color="black" width={12} height={15} />
        }
        menu={{
          items: [
            {
              key: 'sync',
              label: 'Sync by listing',
              onClick: () => setSyncByListingModalVisible(true),
              disabled: !distinctListings || distinctListings.length === 0,
            },
          ],
        }}
        onClick={onSyncClick}
        disabled={loading}
      >
        {buttonText}
      </AntdDropdown.Button>
    );
  }
  if (selectedMenuOption === 'Buyer' || selectedMenuOption === 'In Person') {
    return (
      <StyledSyncButton onClick={onSyncClick}>
        <Icon type="antdCalendarOutlined" color="black" width={14} />
        {buttonText}
      </StyledSyncButton>
    );
  }

  return null;
};

const StyledSyncButton = styled(Button)`
  display: flex;
  align-items: center;
`;

export default CalendarSyncButton;
